import React, { FC, useState } from 'react';
import { gql } from '@apollo/client';
import { ActivityPlannerTimeslotFragment, CartStatusEnum, useActivityDetailQuery } from '../../generated/graphql';
import { useRouter } from 'next/router';
import NotFoundError from '../../entities/NotFoundError';
import ActivityPlanner from '../ActivityPlanner';
import useDateParam from '../../hooks/useDateParam';
import LoadingDetail from '../LoadingDetail';
import ActivityTickets from '../ActivityTickets';
import { getTicketInput, useCart } from '../../hooks/useCart';
import { getCartId } from '../../utils/cart';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import StickyBar, { StickyBarThumnail } from '../StickyBar';
import { useIntl } from 'react-intl';
import createImgProps from '../../utils/createImgProps';
import TextButton from '@oberoninternal/travelbase-ds/components/action/TextButton';
import parseDateTime from '../../utils/parseDateTime';
import createTicketText from '../../utils/createTicketText';
import { useUpdateCart } from '../../hooks/useUpdateCart';
import styled from 'styled-components';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import ActivityPlannerModal from '../ActivityPlannerModal';
import Head from 'next/head';
import ActivityBookButton from '../ActivityStickyBar';

export const pageQuery = gql`
    query ActivityIframePlanner($slug: String!) {
        activity(slug: $slug) {
            id
            bookingType
            externalBookingUrl

            jpegThumbnail: listImage {
                transform(config: TILE, format: JPEG) {
                    ...Transform
                }
            }
            webpThumbnail: listImage {
                transform(config: TILE, format: WEBP) {
                    ...Transform
                }
            }
            name

            # particulars section
            descriptionParticulars
        }
    }
`;

const ActivityIframePlanner: FC<React.PropsWithChildren<unknown>> = () => {
    const router = useRouter();
    const slug = router.query.slug as string;
    const ticketModal = useSesame();
    const deviceSize = useDeviceSize();

    const [activityTickets, setActivityTickets] = useState<boolean>(false);
    const [selectedDayIsBookable, setSelectedDayIsBookable] = useState(false);
    const [selectedDay, setSelectedDay] = useDateParam();
    const [timeslot, setSelectedTimeslot] = useState<ActivityPlannerTimeslotFragment | null>(null);

    const [openActivityTickets, setOpenActivityTickets] = useState(false);
    const [openActivityPlanner, setOpenActivityPlanner] = useState(false);

    const intl = useIntl();
    const cart = useCart();

    const currentTickets =
        cart.cartQuery.data?.cart?.status === CartStatusEnum.Open
            ? cart.tickets.filter(item => item.activity?.slug === slug)
            : [];

    const currentTicket = currentTickets.find(ticket => ticket.timeslot?.id === timeslot?.id);

    const ticketText =
        currentTickets.length > 0
            ? intl.formatList(
                  currentTickets.map(ticket => {
                      if (!ticket.timeslot?.id) {
                          return null;
                      }
                      return (
                          <TextButton
                              onClick={() => {
                                  plannerModal.onOpen();
                                  setSelectedDay(parseDateTime(ticket.startDateTime));
                              }}
                              size="tiny"
                              key={ticket.timeslot.id}
                          >
                              {createTicketText(intl, ticket.startDateTime, ticket.rateLines)}
                          </TextButton>
                      );
                  })
              )
            : null;

    const onSetTimeslot = (slot: ActivityPlannerTimeslotFragment | null) => {
        setSelectedTimeslot(slot);
        setActivityTickets(true);
        setOpenActivityTickets(true);
    };
    // Set default value to true to load mobile planner correctly on first visit
    const plannerModal = useSesame(true);

    const { loading, data } = useActivityDetailQuery({
        variables: {
            slug,
        },
    });

    const onActivityBack = () => {
        if (openActivityTickets) {
            setOpenActivityTickets(false);
        } else if (openActivityPlanner && !openActivityTickets) {
            setOpenActivityPlanner(false);
            setOpenActivityTickets(false);
        }
    };

    const onActivityPlanner = () => {
        setOpenActivityPlanner(true);
        setOpenActivityTickets(false);
    };

    const updateCart = useUpdateCart(timeslot!, slug, data?.activity?.id);

    if (loading) {
        return <LoadingDetail withHero={false} />;
    }

    if (!data?.activity) {
        throw new NotFoundError();
    }

    const { jpegThumbnail, webpThumbnail, name, bookingType, externalBookingUrl } = data.activity;

    const buyTickets = async () => {
        if (!getCartId()) {
            return;
        }
        await router.push(`/checkout/extras`);
    };

    const onEdit = () => {
        setActivityTickets(false);
    };

    return (
        <>
            <Head>
                {jpegThumbnail?.transform?.src && <meta property="og:image" content={jpegThumbnail.transform.src} />}
                {jpegThumbnail?.transform?.src && <meta name="twitter:image" content={jpegThumbnail.transform.src} />}
            </Head>
            {deviceSize !== 'mobile' && (
                <>
                    {!activityTickets && (
                        <ActivityPlanner
                            activity={data?.activity}
                            setSelectedDayIsBookable={setSelectedDayIsBookable}
                            slug={slug}
                            selectedDay={selectedDay}
                            setSelectedDay={setSelectedDay}
                            setTimeslot={onSetTimeslot}
                        />
                    )}
                    {activityTickets && (
                        <ActivityTickets
                            activity={data.activity}
                            setTicket={updateCart}
                            onClose={ticketModal.onClose}
                            open={ticketModal.open}
                            timeslot={timeslot}
                            ticket={currentTicket ? getTicketInput(currentTicket) : null}
                            setEdit={onEdit}
                        />
                    )}
                </>
            )}
            {deviceSize === 'mobile' && (
                <ActivityPlannerModal
                    activity={data.activity}
                    slug={slug}
                    selectedDay={selectedDay}
                    setSelectedDay={setSelectedDay}
                    open={plannerModal.open}
                    onClose={plannerModal.onClose}
                    setTimeslot={onSetTimeslot}
                    setSelectedDayIsBookable={setSelectedDayIsBookable}
                    setTicket={updateCart}
                    ticket={currentTicket ? getTicketInput(currentTicket) : null}
                    timeslot={timeslot}
                    setEdit={onEdit}
                    activityTickets={activityTickets}
                    openActivityTickets={openActivityTickets}
                    onActvityBack={onActivityBack}
                    openActivityPlanner={openActivityPlanner}
                    onActivityPlanner={onActivityPlanner}
                    onModalClose={plannerModal.onClose}
                />
            )}
            <StyledStickyBar>
                {jpegThumbnail && webpThumbnail && (
                    <StickyBarThumnail
                        className="gt-l"
                        title={name}
                        thumbnail={{
                            ...createImgProps(jpegThumbnail, webpThumbnail, undefined, { loading: 'lazy' }),
                        }}
                    />
                )}

                <ActivityBookButton
                    bookingType={bookingType}
                    selectedDayIsBookable={selectedDayIsBookable}
                    ticketText={ticketText}
                    externalBookingUrl={externalBookingUrl}
                    onPlannerModalOpen={plannerModal.onOpen}
                    onBuyTickets={buyTickets}
                />
            </StyledStickyBar>
        </>
    );
};

const StyledStickyBar = styled(StickyBar)`
    display: none;

    @media screen and (max-width: 720px) {
        display: block;
    }
`;

export default ActivityIframePlanner;
